export const extractFilename = (path) => {
    if (!path) return "";
    const parts = String(path).split("/");
    return parts[parts.length - 1].replace(/\.[^/.]+$/, "");
};

const normalize3DFile = (file) => {
    if (!file) return { glb: null, html: null, htmlV2: null };
    if (typeof file === "string") return { glb: null, html: file, htmlV2: null };
    return {
        glb: file.glb || null,
        html: file.html || null,
        htmlV2: file.htmlV2 || null,
    };
};

// The in-house viewer is selected only when the cache contains its dedicated
// iSTP2HTML slot. Existing GLB and Glovius selection remains unchanged otherwise.
const resolve3DModel = (file) => {
    const normalized = normalize3DFile(file);
    if (normalized.htmlV2) {
        return { format: "html", engine: "inhouse", path: normalized.htmlV2 };
    }
    if (normalized.glb) return { format: "glb", path: normalized.glb };
    if (normalized.html) return { format: "html", path: normalized.html };
    return null;
};

const normalizeModelPath = (path) =>
    String(path || "")
        .trim()
        .replace(/\\/g, "/")
        .replace(/\/{2,}/g, "/");

const buildModelKey = (model) =>
    `${model.type}|${model.format || ""}|${normalizeModelPath(model.path)}`;

export const collectDirectMachineModelsFromModelData = (modelData = {}) => {
    const models = [];
    const seen = new Set();

    const pushModel = (model) => {
        if (!model?.type || !model?.path) return;
        const key = buildModelKey(model);
        if (seen.has(key)) return;
        seen.add(key);
        models.push(model);
    };

    const main3d = resolve3DModel(modelData["3dfile"]);
    if (main3d) pushModel({ type: "3D", ...main3d });

    (modelData.splitfiles || []).forEach((sf) => {
        const model = resolve3DModel({ glb: sf?.glb, html: sf?.html || sf?.file, htmlV2: sf?.htmlV2 });
        if (model) pushModel({ type: "3D", ...model });
    });

    if (modelData["2dfile"]) {
        pushModel({ type: "2D", format: "2d", path: modelData["2dfile"] });
    }

    return models;
};

export const collectAvailableModelsFromModelData = (modelData = {}) => {
    const models = [];
    const seen = new Set();

    const pushModel = (model) => {
        if (!model?.type || !model?.path) return;
        const key = buildModelKey(model);
        if (seen.has(key)) return;
        seen.add(key);
        models.push(model);
    };

    // Keep counting behavior aligned with MultipleModelViewer: one 3D variant
    // per logical entry, preferring the dedicated in-house HTML slot when present.
    const main3d = resolve3DModel(modelData["3dfile"]);
    if (main3d) pushModel({ type: "3D", ...main3d });

    (modelData.splitfiles || []).forEach((sf) => {
        const model = resolve3DModel({ glb: sf?.glb, html: sf?.html || sf?.file, htmlV2: sf?.htmlV2 });
        if (model) pushModel({ type: "3D", ...model });
    });

    (modelData.boms || []).forEach((bom) => {
        const threeD = resolve3DModel(bom?.["3dfile"]);
        if (threeD) pushModel({ type: "3D", ...threeD });
    });

    if (modelData["2dfile"]) {
        pushModel({ type: "2D", format: "2d", path: modelData["2dfile"] });
    }

    (modelData.boms || []).forEach((bom) => {
        if (bom["2dfile"]) {
            pushModel({ type: "2D", format: "2d", path: bom["2dfile"] });
        }
    });

    return models;
};

export const countAvailableModelsFromModelData = (modelData = {}) =>
    collectAvailableModelsFromModelData(modelData).length;

export const countDirectAvailableModelsFromModelData = (modelData = {}) =>
    collectDirectMachineModelsFromModelData(modelData).length;

export const hasDirect2DModelFromModelData = (modelData = {}) =>
    Boolean(modelData?.["2dfile"]);

export const hasDirect3DModelFromModelData = (modelData = {}) => {
    const main3d = normalize3DFile(modelData?.["3dfile"]);
    return Boolean(main3d.glb || main3d.html || main3d.htmlV2 || (modelData?.splitfiles || []).length > 0);
};

export const hasDirectMachineModelsFromModelData = (modelData = {}) =>
    hasDirect2DModelFromModelData(modelData) || hasDirect3DModelFromModelData(modelData);

export const selectBestDirectModelFromModelData = (modelData = {}) => {
    const models = collectDirectMachineModelsFromModelData(modelData);
    if (!models.length) return null;
    const best = models[0];
    if (best.type === "2D") {
        return { type: "2D", path: best.path };
    }
    return { type: "3D", format: best.format, engine: best.engine, path: best.path };
};

export const selectBestModelFromModelData = (modelData = {}) => {
    const models = collectAvailableModelsFromModelData(modelData);
    if (!models.length) return null;
    const best = models[0];
    if (best.type === "2D") {
        return { type: "2D", path: best.path };
    }
    return { type: "3D", format: best.format, engine: best.engine, path: best.path };
};
